import { Injectable, Optional, Inject } from '@angular/core';
import { Router, Params, ActivatedRoute } from '@angular/router';
import { INavigationService, IAuthorizationService, IContextService, PershingHttpUtil } from '@pershing/pershing-core';
import { ResponsiveState } from 'ngx-responsive';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of, forkJoin, fromEvent } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Location } from '@angular/common';
import { MENU_CONFIG_OPTIONS } from '../tokens/menu-config-options.token';
import { MenuConfigOptions } from '../model/menu-config-options.model';

@Injectable()
export class PortalNavigationService
    implements INavigationService {
    publicMenuConfigObservable$: Observable<any>;
    privateMenuConfigObservable$: Observable<any>;
    private deviceType: string = 'desktop';
    private parentPageId: string;
    private childPageId: string;
    private grandChildPageId: string;
    private fourthChildPageId: string;
    private currentUrl: string;
    private _parentPage: BehaviorSubject<string> = new BehaviorSubject<any>(undefined);
    private _childPage: BehaviorSubject<string> = new BehaviorSubject<any>(undefined);
    private _grandChildPage: BehaviorSubject<string> = new BehaviorSubject<any>(undefined);
    private _fourthChildPage: BehaviorSubject<string> = new BehaviorSubject<any>(undefined);
    private queryParams: Params;
    private menuItems: any[] = [];
    private publicMenuItems: any[] = [];
    private privateMenuItems: any[] = [];
    private menuCache = new Map();
    private urlArray: any[] = [];
    private communityName: string;
    private themeType: string;
    private cacheBust: string;
    private isSitebuilder: boolean = false;
    isModernTheme: boolean = false;
    private iscustomDesktopMenuconfigIBD: boolean = false;
    private iscustomAdaMenuconfigIBD: boolean = false;
    private iscustomMobileMenuconfigIBD: boolean = false;

    constructor(
        private responsiveState: ResponsiveState,
        private http: HttpClient,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private authorizationService: IAuthorizationService,
        private location: Location,
        private contextService: IContextService,
        @Optional() @Inject('AppSettings') appSettings: any,
        @Optional() @Inject(MENU_CONFIG_OPTIONS) private configOptions: MenuConfigOptions) {

        // TODO: We need to centrialize this behavior for all portals
        // Hierarchial Menu Config
        this.isSitebuilder = appSettings.isSitebuilder;
        if (this.isSitebuilder) {
            // Set Device Settings from Spring.Device value from webserver
            this.deviceType = appSettings.deviceType;

            // get the public menu
            if (appSettings.public_menu_config_hierarchy) {
                this.publicMenuConfigObservable$ = this.processMenuHierarchy(appSettings.public_menu_config_hierarchy, true);
            }
            // get the private menu
            if (appSettings.menu_config_hierarchy) {
                this.privateMenuConfigObservable$ = this.processMenuHierarchy(appSettings.menu_config_hierarchy, false);
            }

            // TODO: We need to add setDeviceType to AbstractNavigationService
            fromEvent(window, 'pershing.assembly.edit.changeDevice').subscribe((e: any) => {
                if (e.detail && e.detail.deviceType) {
                    this.deviceType = e.detail.deviceType;
                }
            });
        } else {

            // Why are we setting deviceType this way?
            this.responsiveState.device$.subscribe(data => {
                this.deviceType = data;
            });

            // IBDName CommunityName will be retired
            let parsemap = appSettings.ibdConfigMap;
            this.communityName = parsemap.community;

        }

        this.deviceType = appSettings.deviceType;
        let url = this.location.path(false);
        this.setCurrentUrl(url);
        // this.setPageFromUrl(url);

        this.location.subscribe(data => {
            let currenturl = data.url;
            if (currenturl && currenturl.indexOf('!') !== -1) {
                return;
            }
            this.setCurrentUrl(currenturl);
            this.setPageFromUrl(currenturl);
        });

        this.themeType = appSettings.themeType;
        this.cacheBust = appSettings.cacheBust;
        this.isModernTheme = appSettings.isModernTheme;

        this.iscustomDesktopMenuconfigIBD  = appSettings.iscustomDesktopMenuconfigIBD;
        this.iscustomAdaMenuconfigIBD = appSettings.iscustomAdaMenuconfigIBD;
        this.iscustomMobileMenuconfigIBD = appSettings.iscustomMobileMenuconfigIBD;
    }

    getComingSoonMsgInd() { }
    setComingSoonMsgInd() { }

    private buildMenuCache(menu: any[], parent: string) {
        if (menu && menu.length > 0) {
            for (let i = 0; i < menu.length; i++) {
                const menuItem = menu[i];
                if (menuItem.supportedDevices.includes(this.getDeviceType().toUpperCase())) {
                    menuItem.url = parent + '/' + menuItem.id;
                    this.menuCache.set(menuItem.id, {
                        'name': menuItem.name,
                        'entitlements': menuItem.entitlements,
                        'url': menuItem.url,
                        'publicAccess': menuItem.publicAccess
                    });
                    if (menuItem.children && menuItem.children.length > 0) {
                        this.buildMenuCache(menuItem.children, menuItem.url);
                    }
                }
            }
        }
    }

    // TODO: We need to centrialize this behavior for all portals
    // Hierarchial Menu Config
    private processMenuHierarchy(hierarchy: string[], isPublicAccess: boolean) {
        let observableBatch = [];
        hierarchy.forEach(path => {
            // add each config in the hierarchy to the batch.
            // if there is an error, add an empty array so we can proceed.
            observableBatch.push(this.http.get(path, { responseType: 'json' }).pipe(catchError(() => of([]))));
        });
        return forkJoin(observableBatch).pipe(map(response => {
            // if the response has multiple objects, merge them.
            // otherwise assign the existing response to the public menu
            let mergedResponse;
            if (response.length > 1) {
                mergedResponse = this.mergeMenu(response);
            } else if (response[0] && Array.isArray(response[0])) {
                // make sure that the first element of the response exists and that it is an array before setting it.
                mergedResponse = response[0];
            } else {
                mergedResponse = [];
            }
            if (isPublicAccess) {
                this.publicMenuItems = mergedResponse;
            } else {
                this.privateMenuItems = mergedResponse;
            }
            this.buildMenuCache(mergedResponse, '/nxi');
            console.log('retrieved menu, ', isPublicAccess);
        }));
    }

    // TODO: We need to centrialize this behavior for all portals
    // Hierarchial Menu Config
    private mergeMenu(menus: any[]) {
        let finalMenu = menus.reduce((previous, next) => {
            let mergeRes = this.mergeDeepMenu(previous, next);
            return mergeRes;
        }, {});
        return finalMenu;
    }

    // TODO: We need to centrialize this behavior for all portals
    // Hierarchial Menu Config
    private mergeDeepMenu(previous, next) {
        if (previous && Array.isArray(previous)) {
            // if next is not defined or next is not an array, just return previous
            if (next && Array.isArray(next)) {
                next.forEach(overriddenChild => {
                    // if this page is a child page, update the parent's list, replace this child using systemId
                    if (overriddenChild.parentPageId && overriddenChild.parentPageId !== '') {
                        let parent = this.findPage(previous, overriddenChild.parentPageId);
                        let replaceIndex = parent.children.findIndex(page => page.systemId === overriddenChild.systemId);
                        if (replaceIndex > -1) {
                            // This will avoid index out of bounds exceptions
                            parent[replaceIndex] = overriddenChild;
                        }
                    } else {
                        // If this page is not a child, then update the top-level list.
                        let replaceIndex = previous.findIndex(page => page.systemId === overriddenChild.systemId);
                        if (replaceIndex > -1) {
                            // This will avoid index out of bounds exceptions
                            previous[replaceIndex] = overriddenChild;
                        } else {
                            // This else will add the page(other-disclosures) that doesn't have systemID to the top-level list
                            previous[previous.length] = overriddenChild;
                        }
                    }
                });
            }
            // returning previous since we updated it in-place
            return previous;
        } else if (next && Array.isArray(next)) {
            // if previous is not defined or previous is not an array, just return the next array.
            return next;
        } else {
            return [];
        }
    }

    // TODO: We need to centrialize this behavior for all portals
    // Hierarchial Menu Config
    findPage(menuChildren: any[], pageId: string) {
        let page: any;
        // if the array is available and not empty, iterate through it.
        if (menuChildren && menuChildren.length > 0) {
            menuChildren.forEach(menuChild => {
                // if the page is already found, then we don't need to re-set it.
                if (page === undefined) {
                    if (menuChild.id === pageId) {
                        // if this is the interested page, return it.
                        page = menuChild;
                    } else if (menuChild.children && menuChild.children.length > 0) {
                        // recurse through the children to find the page
                        page = this.findPage(menuChild.children, pageId);
                    }
                }
            });
        }
        return page;
    }

    private entitledMenuItems(items: any[]) {
        /* for (let i = 0; i < items.length; i++) {
            let childCount = 0;
            if (this.authorizationService.isEntitled(items[i].entitlements)) {
                childCount = 1;
                for (let j = 0; j < items[i].children.length; j++) {
                    if (!this.authorizationService.isEntitled(items[i].children[j].entitlements)) {
                        this.remove(items[i].children, items[i].children[j].id);
                        j--;
                    }
                }
            }
            if (childCount === 0) {
                this.remove(items, items[i].id);
                i--;
            }
        }*/
        for (let i = 0; i < items.length; i++) {
            if (this.authorizationService.isEntitled(items[i].entitlements)) {
                if (items[i].children && items[i].children.length > 0) {
                    this.entitledMenuItems(items[i].children);
                }
            } else {
                this.remove(items, items[i].id);
                i--;
            }
        }

    }

    private remove(array, element) {
        for (let i = 0; i < array.length; i++) {
            if (array[i].id === element) {
                array.splice(i, 1);
            }
        }
    }

    private trimQueryParams(url: string): string {
        if (url.indexOf('?') !== -1) {
            url = url.substr(0, url.indexOf('?'));
        }
        return url;
    }

    getChildPageId(): string {
        return this.childPageId;
    }

    getChildPageName(url: string): string {
        url = this.trimQueryParams(url);
        let urlArray = url.split('/');
        if (urlArray[urlArray.length - 1].split('?').length > 1) {
            urlArray[urlArray.length - 1] = urlArray[urlArray.length - 1].split('?')[0];
        } else if (urlArray[urlArray.length - 1].split('#').length > 1) {
            urlArray[urlArray.length - 1] = urlArray[urlArray.length - 1].split('#')[0];
        } else if (urlArray[urlArray.length - 1].split(';').length > 1) {
            urlArray[urlArray.length - 1] = urlArray[urlArray.length - 1].split(';')[0];
        }
        let childPageId = '';
        if (urlArray[3] !== undefined) {
            childPageId = urlArray[3];
            if (childPageId !== undefined || childPageId.indexOf('secondary') !== -1) {
                childPageId = childPageId.replace('(secondary:', '').replace(')', '');
                return childPageId;
            }
        }
        return childPageId;
    }

    getMenuConfigurations(): Observable<any> {
        if (this.isSitebuilder) {
            return this.getMenuConfigurationsSB();
        } else {
            return this.getMenuConfigurationsLegacy();
        }

    }

    // TODO: We need to centrialize this behavior for all portals
    // Hierarchial Menu Config
    getMenuConfigurationsSB(): Observable<any> {
        let url = this.location.path(false);
        this.urlArray = url.split('/');
        return forkJoin(this.publicMenuConfigObservable$, this.privateMenuConfigObservable$).pipe(map(() => {
            if (this.isPublicPageRequest(this.urlArray[this.urlArray.length - 1])) {
                this.menuItems = this.publicMenuItems.slice(0);
            } else {
                this.menuItems = this.privateMenuItems.slice(0);
                this.entitledMenuItems(this.menuItems);
            }
            this.setPageFromUrl(this.currentUrl);
            return this.menuItems;
        }));
    }

    // TODO: We need to centrialize this behavior for all portals
    // Determining if its private or public page
    private isPublicPageRequest(pageId: string): boolean {
        let pageArray = pageId.split('?');
        return this.menuCache.get(pageArray[0]) && this.menuCache.get(pageArray[0]).publicAccess;

    }
    getHostName() {
        return location.host;
    }
    getMenuConfigurationsLegacy(): Observable<any> {
        let url = this.location.path(false);
        this.urlArray = url.split('/');
        let menuConfigPath: string;
        let checkEntitle: boolean;
        let host = this.getHostName();
        var hostUrl = host.split(":");
        var portNum = hostUrl[1];
        var hostNameLocal = hostUrl[0];
        if (this.urlArray[2] !== 'disclosure') {
            if (this.themeType === 'ada') {
                checkEntitle = true;
                let fomattedCommunity = this.communityName;
                if (this.communityName.includes('ada-')) {
                    fomattedCommunity = this.communityName.split('ada-')[1];
                }
                if (this.iscustomAdaMenuconfigIBD) {
                    if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                    menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/menu-config-ada-' + fomattedCommunity + '.json';
                    } else {
                        menuConfigPath = 'lib/assets/menu-config-ada-' + fomattedCommunity + '.json'; 
                    }
                } else {
                    if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                        menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/menu-config-ada.json';
                    } else {
                        menuConfigPath = 'lib/assets/menu-config-ada.json'; 
                    }
                    
                }               
                return this.getMenuConfigService(menuConfigPath, checkEntitle);
            } else if (this.deviceType === 'mobile' && !this.isModernTheme) {
                checkEntitle = true;
                if (this.iscustomMobileMenuconfigIBD) {
                    if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                    menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/menu-config-mobile-' + this.communityName + '.json';
                    } else {
                        menuConfigPath = 'lib/assets/menu-config-mobile-' + this.communityName + '.json';
                    }
                } else {
                    if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                        menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/menu-config-mobile.json';
                    } else {
                        menuConfigPath = 'lib/assets/menu-config-mobile.json';
                    }
                }                  
                return this.getMenuConfigService(menuConfigPath, checkEntitle);
            } else if ((this.deviceType === 'mobile' || this.deviceType === 'tablet') &&  !!this.isModernTheme) {
                checkEntitle = true;
                if (this.iscustomMobileMenuconfigIBD) {
                    if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                    menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/modern/menu-config-mobile-' + this.communityName + '.json';
                    } else {
                        menuConfigPath = 'lib/assets/modern/menu-config-mobile-' + this.communityName + '.json';
                    }
                } else {
                    if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                        menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/modern/menu-config-mobile.json';
                    } else {
                        menuConfigPath = 'lib/assets/modern/menu-config-mobile.json';
                    }
                }
                return this.getMenuConfigService(menuConfigPath, checkEntitle);
            }
             else if (this.isModernTheme) {
                checkEntitle = true;
                if (this.iscustomDesktopMenuconfigIBD) {
                    if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                        menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/modern/menu-config-' + this.communityName + '.json';
                    } else {
                        menuConfigPath = 'lib/assets/modern/menu-config-' + this.communityName + '.json';
                    }
                } else {
                    if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                        menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/modern/menu-config.json';
                    } else {
                        menuConfigPath = 'lib/assets/modern/menu-config.json';
                    }
                }
                return this.getMenuConfigService(menuConfigPath, checkEntitle);
            } else {
                checkEntitle = true;
                if (this.iscustomDesktopMenuconfigIBD) {
                    if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                        menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/menu-config-' + this.communityName + '.json';
                    } else {
                        menuConfigPath = 'lib/assets/menu-config-' + this.communityName + '.json';
                    }
                } else {
                    if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                        menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/menu-config.json';
                    } else {
                        menuConfigPath = 'lib/assets/menu-config.json';
                    }
                } 
                return this.getMenuConfigService(menuConfigPath, checkEntitle);
            }
        } else {
            checkEntitle = false;
            if (this.themeType === 'ada') {
                let fomattedCommunity = this.communityName;
                if (this.communityName.includes('ada-')) {
                    fomattedCommunity = this.communityName.split('ada-')[1];
                }
                if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                    menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/menu-config-prelogin-ada-' + fomattedCommunity + '.json';
                } else {
                    menuConfigPath = 'lib/assets/menu-config-prelogin-ada-' + fomattedCommunity + '.json';
                }
            } else {
                if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                    menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/menu-config-prelogin-' + this.communityName + '.json';
                } else {
                    menuConfigPath = 'lib/assets/menu-config-prelogin-' + this.communityName + '.json';
                }
            }
            return this.getMenuConfigService(menuConfigPath, checkEntitle);
        }
    }

    getMenuConfigService(menuConfigPath: string, checkEntitle: boolean): Observable<any> {
        if (this.configOptions && this.configOptions.basePath !== undefined) {
            menuConfigPath = this.configOptions.basePath + menuConfigPath;
        }
        if (this.cacheBust && this.cacheBust.length > 0) {
            menuConfigPath = `${menuConfigPath}?${this.cacheBust}`;
        }
        return this.http.get(menuConfigPath, { responseType: 'json' })
            .pipe(
                map((data: any[]) => {
                    this.menuItems = data;
                    if (checkEntitle) {
                        this.entitledMenuItems(this.menuItems);
                    }
                    this.setPageFromUrl(this.currentUrl);
                    return this.menuItems;
                }),
                catchError(err => this.handleError(menuConfigPath, checkEntitle, err))
            );
    }
    private handleError(menuConfigPath: string, checkEntitle: boolean, error: any) {
        let host = location.host;
        var hostUrl = host.split(":");
        var portNum = hostUrl[1];
        var hostNameLocal = hostUrl[0];
        if (menuConfigPath.includes('prelogin')) {
            if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                    menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/menu-config-prelogin.json';
            } else {
                menuConfigPath = 'lib/assets/menu-config-prelogin.json';
            }
        } else if (menuConfigPath.includes('ada')) {
            if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/menu-config-ada.json';
            } else {
                menuConfigPath = 'lib/assets/menu-config-ada.json';
            }
        } else if (menuConfigPath.includes('mobile')) {
            if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/menu-config-mobile.json';
            } else {
                menuConfigPath = 'lib/assets/menu-config-mobile.json';
            }
        } else {
            if (hostNameLocal =='localhost' || portNum == '9995' || portNum== '9999') {
                menuConfigPath = 'node_modules/@pershing/nxi-ng-portal-common/lib/assets/menu-config.json';
            } else {
                menuConfigPath = 'lib/assets/menu-config.json';
            }
        }
        console.log(`errorHandle ${error} : ${menuConfigPath}`);
        return this.getMenuConfigService(menuConfigPath, checkEntitle);
        // return observableThrowError(error);
    }
    getDeviceType(): string {
        return this.deviceType;
    }

    getParentPageId(): string {
        return this.parentPageId;
    }

    getGroupName() {
        let groupName: string = 'nxi';
        if (this.urlArray[2] === 'disclosure') {
            groupName = 'nxi/disclosure';
            return groupName;
        } else {
            return groupName;
        }
    }
    checkIsTitle(page): string {
        for (let i = 0; i < page.length; i++) {
            if (page[i].istitle === undefined && (this.deviceType !== 'mobile' || page[i].showdefault)) {
                return page[i].id;
            }
        }
        return '';
    }
    handleNavigation(navigation: any) {
        let args: any[] = [];
        let groupname = this.getGroupName();
        let titleValue: string = '';
        let pageIdValue: string = '';
        let addReloadParam = false;
        if (navigation.fourthchildPage) {
            args.push(this.parentPageId);
            args.push(this.childPageId);
            args.push(this.grandChildPageId);
            args.push(navigation.fourthchildPage.id);
            titleValue = navigation.fourthchildPage.name;
            pageIdValue = navigation.fourthchildPage.id;
        } else if (navigation.grandchildPage) {
            this.contextService.setRouteContext('');
            args.push((navigation.parentPage && navigation.parentPage.id) ? navigation.parentPage.id : this.parentPageId);
            args.push((navigation.childPage && navigation.childPage.id) ? navigation.childPage.id : this.childPageId);
            args.push(navigation.grandchildPage.id);
            // let currentGrandChildPageId = navigation.grandchildPage.id ;
            if (navigation.grandchildPage.children && navigation.grandchildPage.children.length > 0) {
                let defaultfourthChildPageId = navigation.grandchildPage.children[0].id;
                titleValue = navigation.grandchildPage.children[0].name;
                pageIdValue = navigation.grandchildPage.children[0].id;
                args.push(defaultfourthChildPageId);
            }
        } else if (navigation.childPage) {
            this.contextService.setRouteContext('');
            if (this.parentPageId === 'postlogin-security') {
                if (navigation.childPage.id === 'allcommunications' || navigation.childPage.id === 'settings') {
                    this.parentPageId = 'communications';
                }
            }
            args.push((navigation.parentPage && navigation.parentPage.id) ? navigation.parentPage.id : this.parentPageId);
            args.push(navigation.childPage.id);
            if (!navigation.childPage.skipChildNav && navigation.childPage.children && navigation.childPage.children.length > 0) {
                args.push(this.checkIsTitle(navigation.childPage.children));
            }
            titleValue = navigation.childPage.name;
            pageIdValue = navigation.childPage.id;
            // Only for Mobile Navigation
            // Always re-render neworder view without any state
            // when clicked from navigation menu
            if (this.deviceType && this.deviceType === 'mobile') {
                if (navigation.childPage.id === 'neworder' && this.childPageId === 'neworder') {
                    addReloadParam = true;
                }
            }
        } else {
            this.contextService.setRouteContext('');
            let pageId = navigation.parentPage.id;
            args.push(pageId);
            if (navigation.parentPage.children && navigation.parentPage.children.length > 0) {
                pageId = navigation.parentPage.children[0].id;
                args.push(pageId);
                let childPage = navigation.parentPage.children[0];
                if (childPage.children && childPage.children.length > 0) {
                    args.push(this.checkIsTitle(childPage.children));
                }
            }
            if (navigation.parentPage.children.length > 0) {
                titleValue = navigation.parentPage.children[0].name;
                pageIdValue = navigation.parentPage.children[0].id;
            } else {
                titleValue = navigation.parentPage.id;
                pageIdValue = navigation.parentPage.id;
            }
        }
        document.dispatchEvent(new CustomEvent('portal.browserTitleValue', {
            detail: {
                'titleValue': titleValue,
                'pageIdValue': pageIdValue
            }
        }));
        /**  will publish the page navigated only if route it successful i.e router response is true
         *  else if router response is false will navigate to home page(default page)
         */
        let queryString: any;

        let qParams = this.contextService.getRouteContext();
        if (qParams) {
            if (qParams instanceof Object) {
                queryString = qParams;
            } else {
                queryString = Object.assign({}, PershingHttpUtil.getJsonObjectFromQueryString(qParams));
            }
        } else {
            queryString = {};
        }
        let eDocsQueryString = this.appendQueryForEdocs(args);

        if (eDocsQueryString) {
            queryString = Object.assign(queryString, eDocsQueryString);
        }
        // if (this.needPageRefresh(args)) {
        //     let target: string = `/${groupname}`;
        //     args.map((argVal) => {
        //         target = `${target}/${argVal}`;
        //     });
        //     let qs = PershingHttpUtil.getQueryStringFromJsonObject(queryString);
        //     if (qs && qs.length > 0) {
        //         this.publishNavigation(navigation);
        //         console.log(` Page Refresh : ${target}?${qs}`);
        //         location.href = `${target}?${qs}`;
        //     } else {
        //         this.publishNavigation(navigation);
        //         console.log(` Page Refresh : ${target}`);
        //         location.href = `${target}`;
        //     }
        // } else {
        let routeArgs = [];
        if (addReloadParam) {
            let reloadParam = { reload: (new Date).getTime() };
            routeArgs = [groupname, ...args, reloadParam];
        } else {
            routeArgs = [groupname, ...args];
        }
        console.log('NavigationService Route Args ', routeArgs);
        if (Object.keys(queryString).length > 0) {
            this.router.navigate(routeArgs, { queryParams: queryString }).then(response => {
                this.handleNavResponse(response, navigation, groupname, args, queryString);
            });
        } else {
            this.router.navigate(routeArgs).then(response => {
                this.handleNavResponse(response, navigation, groupname, args);
            });
        }
        // }
    }

    haveModChildPage() {
        let isModChildPage = false;
        // Refresh will not be required if the research pages
        // does not have any MOD child pages
        // Eg: For some site, only rateboard will be available under 'Research'
        // In the case, refresh will not be required
        if (this.authorizationService.isEntitled('MOD_MARKETS || MOD_QUOTES_NEWS || MOD_TOOLS')) {
            isModChildPage = true;
        }
        return isModChildPage;
    }
    /*
    * At times, due to timing issue, multipleDefine error is thrown
    * causing Dojo components to not load
    * Refer: https://jira11.bnymellon.net/browse/NS-13495
    * when navigated from pages with F2 components.
    * As a workaround, any navigation from pages with F2 components (research, tools/watchlist)
    * to other pages will be a page refresh instead of a route.navigate
    *
    * This will be taken up with MOD to ensure appropriate handling for
    * SPA App. This is a work around until MOD delivers required changes
    */
    needPageRefresh(args: any[]): boolean {
        // if (this.ibd === '3CR') {
        //     return false;
        // }
        if (this.childPageId === 'watchlist') {
            if (args[0] === 'research') {
                // Any navigation from Watchlist -> Research
                // Need not a page reload - as both pages have F2 components
                return false;
            } else {
                // Tools/WatchList - Any Child navigation should also be a page reload
                // As there might be Dojo components in child pages of Tools tab
                return true;
            }
        } else if (this.parentPageId === 'research' && !this.haveModChildPage()) {
            // If research have no MOD child page
            // no refresh needed
            return false;
        } else if (this.parentPageId === 'research' && (args[0] === 'tools' && args[1] === 'watchlist')) {
            // Any navigation from Research -> Watchlist (From Menu)
            // Need not a page reload - as both pages have F2 components
            return false;
        } else if ((this.parentPageId === 'research' || this.parentPageId === 'tools') && args[0] !== this.parentPageId) {
            // Any navigation from Tools or Research
            // First level ONLY
            // Navigating to Child pages under Research or Tools (except for watchlist - that is covered in first if)
            // need not be a page refresh
            return true;
        } else {
            // Others
            return false;
        }
    }
    appendQueryForEdocs(args: any): string {
        let queryString;
        if (args[args.length - 1] === 'statementsreports') {
            queryString = { viewId: 'stmt' };
        } else if (args[args.length - 1] === 'tradeconfirmations') {
            queryString = { viewId: 'conf' };
        } else if (args[args.length - 1] === 'taxdocuments') {
            queryString = { viewId: 'tax' };
        } else if (args[args.length - 1] === 'notifications') {
            queryString = { viewId: 'addl' };
        }
        return queryString;
    }
    handleNavResponse(response, navigation, groupname, args, queryString?) {
        if (response === true) {
            this.publishNavigation(navigation);
        } else if (response === false) {
            if (this.parentPageId !== 'home') {
                let HOME_PAGE = { 'id': 'home', 'name': 'Home' };
                this.handleNavigation(HOME_PAGE);
            }
        } else {
            // while clicking or navigating to route that is already active
            let reloadParam = { reload: (new Date).getTime() };
            if (queryString) {
                this.router.navigate([groupname, ...args, reloadParam], { queryParams: queryString }).then(response1 => {
                    console.log('Refresh same view with querystring ', response1);
                });
            } else {
                this.router.navigate([groupname, ...args, reloadParam]).then(response1 => {
                    console.log('Refresh same view without querystring ', response1);
                });
            }

        }
    }

    publishNavigation(navigation: any) {
        if (navigation.fourthchildPage) {
            this.setFourthChildPageId(navigation.fourthchildPage.id);
            this.setFourthChildPage(navigation.fourthchildPage);
        } else if (navigation.grandchildPage) {
            this.setGrandChildPageId(navigation.grandchildPage.id);
            this.setGrandChildPage(navigation.grandchildPage);
            if (navigation.grandchildPage.children && navigation.grandchildPage.children.length > 0) {
                let fourthChild = navigation.grandchildPage.children[0];
                this.setFourthChildPageId(fourthChild.id);
                this.setFourthChildPage(fourthChild);
            }
        } else if (navigation.childPage) {
            this.setGrandChildPage(undefined);
            this.setGrandChildPageId(undefined);
            if (navigation.parentPage) {
                this.setParentPageId(navigation.parentPage.id);
                this.setParentPage(navigation.parentPage);
            }
            this.setChildPageId(navigation.childPage.id);
            if (navigation.childPage.hideFromNavigation) {
                this.setGrandChildPage(navigation.childPage);
            } else {
                this.setChildPage(navigation.childPage);
            }
            // Update the first child  of childPage as grandchild if children  exist
            if (!navigation.childPage.skipChildNav && navigation.childPage.children && navigation.childPage.children.length > 0) {
                let firstChild = navigation.childPage.children[0];
                this.setGrandChildPageId(firstChild.id);
                this.setGrandChildPage(firstChild);
            }
        } else {
            this.setParentPageId(navigation.parentPage.id);
            this.setParentPage(navigation.parentPage);
            this.setGrandChildPage(undefined);
            this.setGrandChildPageId(undefined);
            this.setChildPage(undefined);
            this.setChildPageId(undefined);
            if (navigation.parentPage.children && navigation.parentPage.children.length > 0) {
                let firstChild = navigation.parentPage.children[0];
                this.setChildPage(firstChild);
                this.setChildPageId(firstChild.id);
                if (firstChild.children && firstChild.children.length > 0) {
                    let firstGrandChild = firstChild.children[0];
                    this.setGrandChildPageId(firstGrandChild.id);
                    this.setGrandChildPage(firstGrandChild);
                }
            }

        }
    }
    setChildPageId(childId: string): void {
        if (childId) {
            sessionStorage.setItem('nav_current_pageid', childId);
        }
        this.childPageId = childId;
    }
    setParentPageId(pageId: string): void {
        if (pageId) {
            sessionStorage.setItem('nav_current_pageid', pageId);
        }
        this.parentPageId = pageId;
    }
    setParentPage(parentPage: any) {
        this._parentPage.next(parentPage);
    }
    getParentPage(): any {
        return this._parentPage.asObservable();
    }

    setChildPage(childpage: any) {
        this._childPage.next(childpage);
    }
    getChildPage(): any {
        return this._childPage.asObservable();
    }
    setGrandChildPageId(grandChildPageId: string): void {
        if (grandChildPageId) {
            sessionStorage.setItem('nav_current_pageid', grandChildPageId);
        }
        this.grandChildPageId = grandChildPageId;
    }
    getGrandChildPageId(): string {
        return this.grandChildPageId;
    }
    setGrandChildPage(grandChildPage: any) {
        this._grandChildPage.next(grandChildPage);
    }
    getGrandChildPage(): any {
        return this._grandChildPage.asObservable();
    }

    setFourthChildPageId(fourthChildPageId: string): void {
        if (fourthChildPageId) {
            sessionStorage.setItem('nav_current_pageid', fourthChildPageId);
        }
        this.fourthChildPageId = fourthChildPageId;
    }
    getFourthChildPageId(): string {
        return this.fourthChildPageId;
    }
    setFourthChildPage(fourthChildPage: any) {
        this._fourthChildPage.next(fourthChildPage);
    }
    getFourthChildPage(): any {
        return this._fourthChildPage.asObservable();
    }

    setQueryParams(url: string): void {
        let params = this.parseQueryString(url);
        this.queryParams = params;
    }
    private parseQueryString(url: string): Params {

        let params = {}, queries: any, temp: any, i: any, l: any;
        if (url.indexOf('?') !== -1) {
            let queryString = url.substring(url.indexOf('?') + 1, url.length + 1);
            // Split into key/value pairs
            queries = queryString.split('&');
            // Convert the array of strings into an object
            for (i = 0, l = queries.length; i < l; i++) {
                temp = queries[i].split('=');
                params[temp[0]] = temp[1];
            }
        }
        return params;
    }
    getQueryParams(): Params {
        return this.queryParams;
    }

    handleNavigationWithDomain(domain: string) {
    }

    setCurrentUrl(url: string): void {
        this.currentUrl = url;
    }

    getCurrentUrl(): any {
        return this.currentUrl;
    }
    /**
     * Implemented for supporting GOTO feature
     * Sets the passed domain and value in the contextservice
     * get the qParam from contextservice
     * Navigates to targetUrl passed with/without qparam
     * @param domain
     * @param value
     * @param targetUrl - should contain the full page url wiht groupname
     * eg. for market page setPageFromUrl('/nxi/research/markets/usmarkets')
     */
    setContextAndNavigate(domain: string, domainvalue: any, targetUrl: string): any {
        let pageReload = false;
        if (targetUrl) {
            if (targetUrl.split('#').length > 1) {
                targetUrl = targetUrl.split('#')[0];
            }
            if (targetUrl.split(';').length > 1) {
                targetUrl = targetUrl.split(';')[0];
            }
            this.router.events.subscribe((url: any) => {
                if (url.url) {
                    this.currentUrl = url.url;
                }
            });
            console.log('current url value --- ' + this.currentUrl);
            if (this.currentUrl.includes(targetUrl) && !this.currentUrl.includes('?viewId')) {
                pageReload = true;
                console.log('pageReload-- ' + pageReload);
            }
            this.setCurrentUrl(targetUrl);
            this.setPageFromUrl(targetUrl);
            let qParams: any = {};
            let qParamsaccoutsuppresd: string = '';
            let qParamsaccouts: any = [];
			let accoutparam: string = '';
			let accountParamKey: string = '';
            if (this.contextService.getRouteContext()) {
                qParamsaccoutsuppresd = this.contextService.getRouteContext();
                if (qParamsaccoutsuppresd.indexOf('&') !== -1) {
                    qParamsaccouts = qParamsaccoutsuppresd.split('&');
                    qParamsaccouts.forEach(function (item) {

                        if (item.indexOf('=') !== -1 && item.split('=')[1].length >= 64) {
							accoutparam = item.split('=')[1];
							accountParamKey = item.split('=')[0];
                        }

                    });

                } else {
                    if (qParamsaccoutsuppresd.indexOf('=') !== -1 && qParamsaccoutsuppresd.split('=')[1].length >= 64) {
						accoutparam = qParamsaccoutsuppresd.split('=')[1];
						accountParamKey = qParamsaccoutsuppresd.split('=')[0];
                    }
                }
                if (accoutparam) {
					qParams = PershingHttpUtil.getJsonObjectFromQueryString(qParamsaccoutsuppresd);
					delete qParams[accountParamKey];
                } else {
                    qParams = PershingHttpUtil.getJsonObjectFromQueryString(qParamsaccoutsuppresd);
                }
            }

            let args = targetUrl.split('/');
            let isModernCommunicationRoute = this.isModernTheme && args && args[2] === 'communications';
            if ((args[2] === 'communications' && args[3] === 'allcommunications') || (this.themeType === 'ada' && args[2] === 'communications' && args[3] === 'alldocuments') || isModernCommunicationRoute) {
                let eDocsQueryString = this.appendQueryForEdocs(args);
                if (eDocsQueryString) {
                    qParams = Object.assign(qParams, eDocsQueryString);
                }
            }
            if (domain && domainvalue) {
                this.contextService.setDomainContext(domain, domainvalue);
            }
            // let previousUrl = this.router.url;
            // if (!(this.ibd === '3CR') && (previousUrl.includes('/nxi/research') || previousUrl.includes('/nxi/tools')) &&
            //     !(targetUrl.includes('/nxi/research') || targetUrl.includes('/nxi/exchangeagreementoverview'))) {
            //     let target = `${targetUrl}`;
            //     if (qParams) {
            //         let qs = PershingHttpUtil.getQueryStringFromJsonObject(qParams);
            //         target = `${target}?${qs}`;
            //     }
            //     target = `${target}#main-container`;
            //     console.log(`Page Refresh target ${target}`);
            //     location.href = target;
            // } else {
            if (pageReload) {
                // Reload same route - add a reload param to re-render the route
                let reloadParam = { reload: (new Date).getTime() };
                if (qParams) {
                    this.router.navigate([targetUrl, reloadParam], { queryParams: qParams, fragment: 'main-container' });
                } else {
                    this.router.navigate([targetUrl, reloadParam], { fragment: 'main-container' });
                }
            } else {
                if (qParams) {
                    return this.router.navigate([targetUrl], { queryParams: qParams, fragment: 'main-container' });
                } else {
                    return this.router.navigate([targetUrl], { fragment: 'main-container' });
                }
            }
        }
        // }
    }
    /**
     * Set the page object and page values from the url
     * @param url url - should start with the groupname. eg. for market page
     * setPageFromUrl('/nxi/research/markets/usmarkets')
     */
    setPageFromUrl(url: string) {
        let urlArray = url.split('/');
        if (urlArray[urlArray.length - 1].split('?').length > 1) {
            urlArray[urlArray.length - 1] = urlArray[urlArray.length - 1].split('?')[0];
        } else if (urlArray[urlArray.length - 1].split('#').length > 1) {
            urlArray[urlArray.length - 1] = urlArray[urlArray.length - 1].split('#')[0];
        } else if (urlArray[urlArray.length - 1].split(';').length > 1) {
            urlArray[urlArray.length - 1] = urlArray[urlArray.length - 1].split(';')[0];
        }
        if (urlArray[2] === 'disclosure') {
            this.setParentPageId(urlArray[3]);
            this.setChildPageId(urlArray[4]);
            this.setGrandChildPageId(urlArray[5]);
        } else {
            // if (url.includes('communications') && (this.deviceType === 'mobile') && urlArray.length === 4) {
            //     this.setParentPageId(urlArray[1]);
            //     this.setChildPageId(urlArray[2]);
            // } else {
            this.setParentPageId(urlArray[2]);
            this.setChildPageId(urlArray[3]);
           // }
            this.setGrandChildPageId(urlArray[4]);
            this.setFourthChildPageId(urlArray[5]);
        }
        let pageId = this.childPageId;
        console.log('setPageFromUrl method--' + pageId);
        if (pageId) {
            console.log('setPageFromUrl method menuitem--' + this.menuItems);
            for (let items of this.menuItems) {
                let result;
                for (let children of items.children) {
                    if (children.id === pageId) {
                        result = children;
                        this.setParentPage(items);
                        if (result.children && result.children.length > 0) {
                            this.setChildPage(result);
                            let grandChildpage = result.children.filter(page => page.id === this.grandChildPageId)[0];
                            if (!grandChildpage && !result.skipChildNav) {
                                grandChildpage = result.children[0];
                            }
                            this.setGrandChildPage(grandChildpage);
                            if (grandChildpage && grandChildpage.children && grandChildpage.children.length > 0) {
                                let fourthChildpage = grandChildpage.children.filter(page => page.id === this.fourthChildPageId)[0];
                                this.setFourthChildPage(fourthChildpage);
                            }
                        } else {
                            if (result.hideFromNavigation) {
                                this.setGrandChildPage(result);
                            } else {
                                this.setChildPage(result);
                                this.setGrandChildPage(undefined);
                            }
                        }
                        break;
                    }
                }
                if (result !== undefined) {
                    break;
                }
            }
        } else {
            pageId = this.parentPageId;
            console.log('setPageFromUrl method else case --' + pageId);
            let result = this.menuItems.filter(menuItem => menuItem.id === pageId)[0];
            if (result) {
                this.setParentPage(result);
            }
        }
        document.dispatchEvent(new CustomEvent('portal.browserTitleValue', {
            detail: {
                'titleValue': pageId,
                'pageIdValue': pageId
            }
        }));
    }
    navigateOnContext() {

    }
    navigateOnAction() {

    }
}
